<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';
import KnowledgeSourcesAPI from 'dashboard/api/pipelines/knowledgeSources';

const props = defineProps({
  knowledgeBase: { type: Object, required: true },
});

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const linkedInboxes = ref([]);
const showAddModal = ref(false);
const selectedInboxIds = ref([]);

const accountId = computed(() => store.getters.getCurrentAccountId);
const allInboxes = computed(() => store.getters['inboxes/getInboxes'] || []);

const availableInboxes = computed(() =>
  allInboxes.value.filter(
    inbox => !linkedInboxes.value.some(li => li.id === inbox.id)
  )
);

const channelIcon = channelType => ({
  'Channel::WebWidget': 'i-lucide-globe',
  'Channel::Api': 'i-lucide-webhook',
  'Channel::Email': 'i-lucide-mail',
  'Channel::Whatsapp': 'i-lucide-message-circle',
  'Channel::Sms': 'i-lucide-message-square',
  'Channel::FacebookPage': 'i-lucide-facebook',
  'Channel::Instagram': 'i-lucide-instagram',
  'Channel::Telegram': 'i-lucide-send',
}[channelType] || 'i-lucide-inbox');

const loadInboxes = async () => {
  isLoading.value = true;
  try {
    const response = await KnowledgeSourcesAPI.getInboxes(accountId.value, props.knowledgeBase.id);
    linkedInboxes.value = response.data || [];
  } catch {
    useAlert(t('KNOWLEDGE_BASE.INBOXES.LOAD.ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const addInboxes = async () => {
  if (!selectedInboxIds.value.length) return;
  try {
    await KnowledgeSourcesAPI.addInboxes(accountId.value, props.knowledgeBase.id, selectedInboxIds.value);
    await loadInboxes();
    useAlert(t('KNOWLEDGE_BASE.INBOXES.ADD.SUCCESS'));
    showAddModal.value = false;
    selectedInboxIds.value = [];
  } catch {
    useAlert(t('KNOWLEDGE_BASE.INBOXES.ADD.ERROR'));
  }
};

const removeInbox = async inbox => {
  try {
    await KnowledgeSourcesAPI.removeInbox(accountId.value, props.knowledgeBase.id, inbox.id);
    linkedInboxes.value = linkedInboxes.value.filter(i => i.id !== inbox.id);
    useAlert(t('KNOWLEDGE_BASE.INBOXES.REMOVE.SUCCESS'));
  } catch {
    useAlert(t('KNOWLEDGE_BASE.INBOXES.REMOVE.ERROR'));
  }
};

const toggleInboxSelection = id => {
  const idx = selectedInboxIds.value.indexOf(id);
  if (idx === -1) selectedInboxIds.value.push(id);
  else selectedInboxIds.value.splice(idx, 1);
};

onMounted(async () => {
  await store.dispatch('inboxes/get');
  await loadInboxes();
});
</script>

<template>
  <div class="space-y-4">
    <div class="flex items-center justify-between">
      <div>
        <h3 class="text-base font-semibold text-n-slate-12 m-0">
          {{ $t('KNOWLEDGE_BASE.INBOXES.TITLE') }}
        </h3>
        <p class="text-sm text-n-slate-11 mt-0.5 m-0">
          {{ $t('KNOWLEDGE_BASE.INBOXES.DESCRIPTION') }}
        </p>
      </div>
      <Button
        icon="i-lucide-plus"
        :label="$t('KNOWLEDGE_BASE.INBOXES.ADD.BUTTON')"
        size="sm"
        @click="showAddModal = true"
      />
    </div>

    <div v-if="isLoading" class="flex justify-center py-10">
      <Spinner />
    </div>

    <!-- Inboxes vinculadas -->
    <div v-else-if="linkedInboxes.length" class="divide-y divide-n-weak">
      <div
        v-for="inbox in linkedInboxes"
        :key="inbox.id"
        class="py-3 flex items-center gap-3"
      >
        <span class="w-8 h-8 shrink-0 flex items-center justify-center rounded-lg bg-n-alpha-2">
          <span :class="`i-lucide w-4 h-4 text-n-slate-11 ${channelIcon(inbox.channel_type)}`" />
        </span>
        <div class="flex-1 min-w-0">
          <p class="text-sm font-medium text-n-slate-12 truncate m-0">{{ inbox.name }}</p>
          <p class="text-xs text-n-slate-11 m-0">{{ inbox.channel_type }}</p>
        </div>
        <Button
          v-tooltip.top="$t('KNOWLEDGE_BASE.INBOXES.REMOVE.BUTTON')"
          icon="i-lucide-x"
          xs
          ruby
          faded
          @click="removeInbox(inbox)"
        />
      </div>
    </div>

    <div v-else-if="!isLoading" class="text-center py-6">
      <p class="text-sm text-n-slate-11">{{ $t('KNOWLEDGE_BASE.INBOXES.EMPTY') }}</p>
    </div>
  </div>

  <!-- Modal: Adicionar inboxes -->
  <woot-modal v-model:show="showAddModal" :on-close="() => (showAddModal = false)">
    <div class="flex flex-col gap-4 p-6">
      <h3 class="text-lg font-semibold text-n-slate-12 m-0">
        {{ $t('KNOWLEDGE_BASE.INBOXES.ADD.MODAL_TITLE') }}
      </h3>

      <div v-if="!availableInboxes.length" class="text-center py-4">
        <p class="text-sm text-n-slate-11">{{ $t('KNOWLEDGE_BASE.INBOXES.ADD.NONE_AVAILABLE') }}</p>
      </div>

      <div v-else class="divide-y divide-n-weak max-h-72 overflow-y-auto">
        <label
          v-for="inbox in availableInboxes"
          :key="inbox.id"
          class="flex items-center gap-3 py-3 cursor-pointer"
        >
          <input
            type="checkbox"
            :value="inbox.id"
            :checked="selectedInboxIds.includes(inbox.id)"
            @change="toggleInboxSelection(inbox.id)"
          />
          <span :class="`i-lucide w-4 h-4 text-n-slate-11 ${channelIcon(inbox.channel_type)}`" />
          <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
        </label>
      </div>

      <div class="flex justify-end gap-2 mt-2">
        <Button
          variant="ghost"
          :label="$t('KNOWLEDGE_BASE.FORM.CANCEL')"
          @click="showAddModal = false"
        />
        <Button
          :label="$t('KNOWLEDGE_BASE.INBOXES.ADD.CONFIRM')"
          :disabled="!selectedInboxIds.length"
          @click="addInboxes"
        />
      </div>
    </div>
  </woot-modal>
</template>
